import React, { useState, useEffect, useMemo, useRef } from 'react';
import { auth, db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, where, orderBy, doc, getDoc } from 'firebase/firestore';
import { loadLibraryPayload } from '../services/libraryPayloadService';
import KesimpulanAiMakmal from './KesimpulanAiMakmal';
import ArchV2Makmal from './ArchV2Makmal';
import { gambarProduk } from '../services/promptArchV2Service';
import { binaTeksSalinan, salinKeClipboard } from '../utils/salinOutputMakmal';

// ═══════════════════════════════════════════════════════════
//  PIPELINE TEST — Makmal ujian jimat OpenAI  (/pipeline-test)
//  Panggil Cloud Function `pipelineTestGenerate` (admin sahaja).
//  Jalankan pipeline SEKARANG vs JIMAT sebelah-menyebelah TANPA
//  sentuh workspace: tak potong kuota, tak tulis produk, tak kacau data kos.
// ═══════════════════════════════════════════════════════════

const LIB_FOLDER = 'product';

const usd = (x) => '$' + Number(x || 0).toFixed(4);
const savingsPct = (cur, opt) => (cur > 0 ? Math.round((1 - opt / cur) * 100) : 0);

// Unjuran volume: 2,000 call pipeline sehari (anggaran kadar RM4.50/USD)
const DAILY_CALLS = 2000;
const USD_TO_MYR = 4.5;
const usdDaily = (c) => '$' + ((c || 0) * DAILY_CALLS).toFixed(2);
const rmMonthly = (c) => 'RM' + Math.round((c || 0) * DAILY_CALLS * 30 * USD_TO_MYR).toLocaleString();

function MetricTile({ label, value, good }) {
  return (
    <div className="rounded-lg bg-slate-50 px-4 py-3">
      <p className="text-[12px] text-slate-500 m-0">{label}</p>
      <p className={`text-[24px] font-semibold m-0 mt-0.5 ${good ? 'text-emerald-600' : 'text-slate-800'}`}>{value}</p>
    </div>
  );
}

function OutputBlock({ col, accent }) {
  if (col?.creative?.error) {
    return <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2.5 text-[12.5px] text-rose-700">Creative gagal: {col.creative.error}</div>;
  }
  const sets = col?.creative?.sets || [];
  if (!sets.length) {
    const fr = col?.creative?.finishReason;
    const rt = col?.creative?.reasoningTokens;
    const truncated = fr === 'length';
    return (
      <div className={`rounded-lg px-3 py-2.5 text-[12.5px] ${truncated ? 'bg-amber-50 border border-amber-200 text-amber-800' : 'bg-slate-50 text-slate-500'}`}>
        {truncated
          ? <>⚠ Output <b>habis had token</b> (finish: length){rt ? <> — reasoning makan <b>{Number(rt).toLocaleString()}</b> token</> : ''}. JSON tak sempat siap → set tak terbaca.<br /><b>Fix:</b> turunkan reasoning ke <b>minimal</b> (bebaskan bajet), atau naikkan <b>Max output tokens</b> (cuba ~2500).</>
          : <>Tiada set dikenali{fr ? ` (finish: ${fr})` : ''}. Raw output:</>}
        <pre className="mt-1 text-[11px] text-slate-600 whitespace-pre-wrap break-words max-h-40 overflow-y-auto">{col?.creative?.rawPreview || '(content kosong — semua token pergi ke reasoning)'}</pre>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {sets.map((s, i) => (
        <div key={i} className="rounded-lg bg-slate-50 px-3 py-2.5">
          <p className={`text-[11px] font-bold uppercase tracking-wide m-0 mb-1 ${accent ? 'text-indigo-500' : 'text-slate-400'}`}>Set {i + 1}</p>
          {s.hookText
            ? <p className="text-[12.5px] m-0 mb-1"><span className="text-slate-400">Hook:</span> <span className="font-semibold text-slate-700">{s.hookText}</span></p>
            : s.hookMissing
              ? <p className="text-[11.5px] text-amber-600 m-0 mb-1">⚠ AI tak beri hook — prompt kekal [HOOK]</p>
              : null}
          {s.promptImage && <p className="text-[12.5px] text-slate-600 leading-relaxed m-0 whitespace-pre-wrap"><span className="text-slate-400">Prompt gambar (final):</span> {s.promptImage}</p>}
          {s.videoScript && <p className="text-[12.5px] text-slate-600 leading-relaxed m-0 mt-1"><span className="text-slate-400">Skrip dialog:</span> {s.videoScript}</p>}
          {s.captionTiktok && <p className="text-[12.5px] text-slate-600 leading-relaxed m-0 mt-1"><span className="text-slate-400">Caption TikTok:</span> {s.captionTiktok}</p>}
          {s.promptVideo && (
            <details className="mt-1">
              <summary className="text-[12px] text-indigo-500 cursor-pointer">Prompt video penuh</summary>
              <p className="text-[12px] text-slate-500 leading-relaxed m-0 mt-1 whitespace-pre-wrap">{s.promptVideo}</p>
            </details>
          )}
          {s.scenes && s.scenes.length > 0 && (
            <div className="mt-1.5 space-y-1.5">
              {s.scenes.map((sc) => (
                <div key={sc.scene} className="border-l-2 border-slate-200 pl-2">
                  <p className="text-[12px] text-slate-600 m-0"><span className="text-slate-400">Babak {sc.scene} — skrip:</span> {sc.videoScript || '—'}</p>
                  <details className="mt-0.5">
                    <summary className="text-[11.5px] text-indigo-500 cursor-pointer">Prompt video babak {sc.scene}</summary>
                    <p className="text-[11.5px] text-slate-500 whitespace-pre-wrap m-0 mt-1">{sc.promptVideo}</p>
                  </details>
                </div>
              ))}
            </div>
          )}
          {!s.promptImage && !s.captionTiktok && <p className="text-[12px] text-slate-400 m-0">{JSON.stringify(s).slice(0, 200)}</p>}
        </div>
      ))}
    </div>
  );
}

// Butang "Copy Full Output" (tab Ujian Master Prompt) — teks dibina oleh utils/salinOutputMakmal.js.
function ButangSalinOutput({ binaTeks }) {
  const [keadaan, setKeadaan] = useState(null); // null | 'ok' | 'gagal'
  const klik = async () => {
    const ok = await salinKeClipboard(binaTeks());
    setKeadaan(ok ? 'ok' : 'gagal');
    setTimeout(() => setKeadaan(null), 2000);
  };
  return (
    <button onClick={klik}
      className={`h-7 flex items-center gap-1 px-2.5 text-[11.5px] font-semibold rounded-lg border whitespace-nowrap ${keadaan === 'gagal' ? 'border-rose-200 text-rose-600 bg-rose-50' : keadaan === 'ok' ? 'border-emerald-200 text-emerald-700 bg-emerald-50' : 'border-slate-200 text-slate-600 bg-white hover:bg-slate-50'}`}>
      <span className="material-symbols-outlined text-[14px]">{keadaan === 'ok' ? 'check' : 'content_copy'}</span>
      {keadaan === 'ok' ? 'Copied' : keadaan === 'gagal' ? 'Copy failed' : 'Copy Full Output'}
    </button>
  );
}

function ResultColumn({ title, icon, col, accent, note, binaTeksSalinan }) {
  return (
    <div className={`rounded-xl bg-white p-5 ${accent ? 'border-2 border-indigo-500' : 'border border-slate-200'}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`material-symbols-outlined text-[18px] ${accent ? 'text-indigo-600' : 'text-slate-500'}`}>{icon}</span>
          <p className="text-[15px] font-semibold text-slate-800 m-0">{title}</p>
        </div>
        <div className="flex items-center gap-2">
          {binaTeksSalinan && <ButangSalinOutput binaTeks={binaTeksSalinan} />}
          {accent && <span className="text-[11px] bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-lg font-bold">{col?.creative?.reasoning || 'jimat'}</span>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-2">
        <MetricTile label="Kos / call" value={usd(col?.totalCostUSD)} good={accent} />
        <MetricTile label="Token penulis" value={Number(col?.writerOutputTokens || 0).toLocaleString()} good={accent} />
      </div>
      <p className="text-[12px] text-slate-500 m-0 mb-3">@ {DAILY_CALLS.toLocaleString()} call/hari ≈ <span className="font-bold text-slate-700">{usdDaily(col?.totalCostUSD)}/hari</span> · <span className="font-bold text-slate-700">{rmMonthly(col?.totalCostUSD)}/bulan</span></p>
      <div className="flex flex-wrap gap-2 mb-4 text-[11px]">
        <span className="bg-indigo-50 text-indigo-700 font-semibold px-2 py-0.5 rounded">model: {col?.creative?.model || '—'}</span>
        <span className="bg-slate-100 text-slate-500 px-2 py-0.5 rounded">reasoning: {col?.creative?.reasoning || '—'}</span>
        {col?.creative?.ayatWajib === false && <span className="bg-amber-50 text-amber-700 font-semibold px-2 py-0.5 rounded">ayat wajib OFF</span>}
        {col?.creative?.vision && <span className="bg-indigo-50 text-indigo-700 font-semibold px-2 py-0.5 rounded">vision ON · gambar termasuk dalam kos{col?.creative?.inputTokens ? ` · ${Number(col.creative.inputTokens).toLocaleString()} token in` : ''}</span>}
        <span className="bg-slate-100 text-slate-500 px-2 py-0.5 rounded">research: {col?.research?.skipped ? (col?.research?.source ? `data (${col.research.source})` : 'dilangkau') : (col?.research?.model || '—')}</span>
        {col?.research?.error && <span className="bg-rose-50 text-rose-600 px-2 py-0.5 rounded">research err</span>}
      </div>
      {note && <p className="text-[12px] text-amber-600 m-0 mb-3">{note}</p>}
      <OutputBlock col={col} accent={accent} />
    </div>
  );
}

// ── Model creative yang boleh diuji (kolum kanan, tab Ujian Master Prompt) ──
// Senarai ni MESTI padan dgn LAB_SELECTABLE_CREATIVE_MODELS di functions/index.js —
// server tapis semula, jadi id yang tak sah akan jatuh balik ke gpt-5-mini.
const LAB_MODELS = [
  { id: 'gpt-5-mini', label: 'gpt-5-mini · production' },
  { id: 'gpt-5.6-luna', label: 'gpt-5.6-luna · calon murah' },
  { id: 'gpt-6-luna', label: 'gpt-6-luna · calon baharu, paling murah' },
];

// Keluarga gpt-5.6 + gpt-6 panggil tahap terendah 'none'; gpt-5 lama panggil 'minimal'.
// Hantar nama salah = OpenAI tolak panggilan, jadi pilihan bertukar ikut model.
// ⚠️ KEMBAR: labReasoningLevels/labNormalizeReasoning dlm functions/index.js.
const modelBaharu = (m) => String(m).startsWith('gpt-5.6') || String(m).startsWith('gpt-6');
const reasoningLevelsFor = (m) => modelBaharu(m)
  ? ['none', 'low', 'medium', 'high', 'xhigh', 'max']
  : ['minimal', 'low', 'medium', 'high'];
const normalizeReasoningFor = (m, lv) => {
  const baharu = modelBaharu(m);
  let x = String(lv || '');
  if (baharu && x === 'minimal') x = 'none';
  if (!baharu && x === 'none') x = 'minimal';
  return reasoningLevelsFor(m).includes(x) ? x : 'low';
};

// Versi bentuk setting tersimpan (localStorage pipelineTest_state_v1) — lihat effect pulih.
const SETTING_V = 2;

// Data produk yang disuap kepada AI, sebagai teks — untuk Kesimpulan AI menyemak dakwaan.
function teksDataProduk(p) {
  const intel = p?.intelligenceData || {};
  const bahagian = [];
  if (intel.imageAnalysis) bahagian.push('[VISION ANALYSIS]\n' + intel.imageAnalysis);
  if (intel.webResearch) bahagian.push('[WEB RESEARCH]\n' + intel.webResearch);
  else if (p?.scrapedData) bahagian.push('[SCRAPED DATA]\n' + JSON.stringify(p.scrapedData));
  return bahagian.join('\n\n');
}

export default function PipelineTestView() {
  const [topTab, setTopTab] = useState('pipeline'); // 'pipeline' | 'prompt' | 'archv2'
  // Tab "Ujian Prompt Architecture V2" (ArchV2Makmal.jsx) dipasang kali pertama dibuka, kemudian
  // kekal terpasang (disorok) supaya run yang sedang berjalan / suntingan pustaka tidak hilang.
  const [v2Dibuka, setV2Dibuka] = useState(false);
  const [mode, setMode] = useState('full'); // 'full' = Stage 1+2+3 · 'stage3' = Stage 3 dari data scrape
  const [currentMasterPrompt, setCurrentMasterPrompt] = useState('');
  const [newPrompt, setNewPrompt] = useState('');
  const [promptModel, setPromptModel] = useState('gpt-5-mini');
  // Lalai = setting production (low / low / 8000). Lalai lama (minimal / 1400) memotong
  // output di tengah → nampak macam gagal walhal cuma kehabisan token.
  const [promptReasoning, setPromptReasoning] = useState('low');
  const [promptVerbosity, setPromptVerbosity] = useState('low');
  const [promptMaxTokens, setPromptMaxTokens] = useState(8000);
  // Vision (2026-10-15): kolum KANAN hantar gambar produk terus kepada AI (kolum kiri = production, tanpa gambar).
  const [promptVision, setPromptVision] = useState(false);
  // Ayat wajib module (2026-10-15): kolum KANAN boleh dimatikan untuk uji master prompt tanpa gangguan.
  // Lalai ON = sama macam production. Kolum kiri sentiasa ON.
  const [promptAyatWajib, setPromptAyatWajib] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [modules, setModules] = useState([]);
  const [selectedModuleId, setSelectedModuleId] = useState('');
  const [sysItems, setSysItems] = useState([]);
  const [ownItems, setOwnItems] = useState([]);
  const [libLoading, setLibLoading] = useState(false);
  const [libTab, setLibTab] = useState('system'); // 'system' | 'own'
  const [libSort, setLibSort] = useState('date'); // 'number' | 'name' | 'date' — sama macam LibraryView
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [product, setProduct] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [runs, setRuns] = useState([]); // sejarah run — auto-save localStorage (survive PIN lock/reload)
  const draftsRef = useRef({});             // draf prompt baru per module
  const restoredModuleRef = useRef(null);   // module dipulihkan dari simpanan
  const restoredTemplateRef = useRef(null); // template dipulihkan dari simpanan

  // ── PULIH state + sejarah dari localStorage (survive PIN lock / reload) ──
  useEffect(() => {
    try {
      const s = JSON.parse(localStorage.getItem('pipelineTest_state_v1') || 'null');
      if (s) {
        if (s.topTab) setTopTab(s.topTab);
        if (s.topTab === 'archv2') setV2Dibuka(true);
        if (s.mode) setMode(s.mode);
        if (s.promptModel) setPromptModel(s.promptModel);
        // Normalize — simpanan lama boleh ada gabungan model/reasoning yang tak sah lagi
        // (cth 'minimal' + Luna), yang akan buat dropdown papar kosong.
        // [2026-10-06] Simpanan sebelum SETTING_V 2 membawa lalai lama (1400 token) → abaikan
        // SEKALI sahaja supaya kolum kanan bermula pada setting production. Draf prompt,
        // produk dan module tetap dipulihkan.
        if (s.settingV === SETTING_V) {
          if (s.promptReasoning) setPromptReasoning(normalizeReasoningFor(s.promptModel || 'gpt-5-mini', s.promptReasoning));
          if (s.promptVerbosity) setPromptVerbosity(s.promptVerbosity);
          if (s.promptMaxTokens) setPromptMaxTokens(s.promptMaxTokens);
        } else if (s.promptModel) {
          setPromptReasoning(normalizeReasoningFor(s.promptModel, 'low'));
        }
        if (s.promptVision === true) setPromptVision(true);
        if (s.promptAyatWajib === false) setPromptAyatWajib(false);
        if (s.product) setProduct(s.product);
        if (s.drafts) draftsRef.current = s.drafts;
        restoredModuleRef.current = s.selectedModuleId || null;
        restoredTemplateRef.current = s.selectedTemplateId || null;
      }
      const r = JSON.parse(localStorage.getItem('pipelineTest_runs_v1') || 'null');
      if (Array.isArray(r) && r.length) { setRuns(r); setResult(r[0]?.result || null); }
    } catch (e) { console.warn('[PipelineTest] restore gagal:', e); }
  }, []);

  // ── SIMPAN state ringan setiap kali berubah ──
  useEffect(() => {
    try {
      localStorage.setItem('pipelineTest_state_v1', JSON.stringify({
        settingV: SETTING_V,
        topTab, mode, selectedModuleId, promptModel, promptReasoning, promptVerbosity, promptMaxTokens, promptVision, promptAyatWajib, selectedTemplateId,
        product: product ? { ...product, imageBase64: null } : null, // buang base64 (besar)
        drafts: draftsRef.current,
      }));
    } catch (e) { /* quota penuh — abaikan */ }
  }, [topTab, mode, selectedModuleId, promptModel, promptReasoning, promptVerbosity, promptMaxTokens, promptVision, promptAyatWajib, selectedTemplateId, product, newPrompt]);

  // ── SIMPAN sejarah run (heavy — tulis bila runs berubah sahaja) ──
  useEffect(() => {
    try { localStorage.setItem('pipelineTest_runs_v1', JSON.stringify(runs)); }
    catch (e) {
      try { localStorage.setItem('pipelineTest_runs_v1', JSON.stringify(runs.slice(0, 5))); } catch (e2) { /* abaikan */ }
    }
  }, [runs]);

  // Load modules dari Firestore (id + nama betul)
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'modules'), orderBy('order', 'asc')));
        const mods = snap.docs.map(d => ({ id: d.id, name: d.data().name || d.id }));
        setModules(mods);
        if (mods.length) setSelectedModuleId(restoredModuleRef.current || mods[0].id);
      } catch (e) {
        console.error('[PipelineTest] load modules gagal:', e);
        setError('Gagal load senarai module: ' + e.message);
      }
    })();
  }, []);

  // Bila module berubah: load master prompt semasa (prefill editor) + template GOYANG.
  useEffect(() => {
    if (!selectedModuleId) return;
    (async () => {
      try {
        // Baca dari PETI BESI dulu (modules/{id}/private/prompt — admin sahaja), fallback field lama
        let raw = null;
        try {
          const privSnap = await getDoc(doc(db, 'modules', selectedModuleId, 'private', 'prompt'));
          if (privSnap.exists()) raw = privSnap.data().masterPrompt ?? null;
        } catch (e2) { /* rules tolak — guna fallback bawah */ }
        if (raw == null) {
          const snap = await getDoc(doc(db, 'modules', selectedModuleId));
          raw = snap.exists() ? (snap.data().masterPrompt ?? '') : '';
        }
        // masterPrompt boleh jadi objek 3 mode — makmal guna Mode 1 (no avatar, no gender)
        const mp = typeof raw === 'string'
          ? raw
          : (raw?.mode1_noAvatarNoGender || Object.values(raw || {}).find(v => typeof v === 'string') || '');
        setCurrentMasterPrompt(mp);
        // Draf tersimpan (survive PIN lock) diutamakan; kalau tiada, prefill prompt semasa
        setNewPrompt(draftsRef.current[selectedModuleId] ?? mp);
      } catch (e) { console.warn('[PipelineTest] load masterPrompt gagal:', e); }
    })();
    if (selectedModuleId === 'goyang') {
      (async () => {
        try {
          const tsnap = await getDocs(collection(db, 'system_templates', selectedModuleId, 'items'));
          const tpl = tsnap.docs.map(d => ({ id: d.id, name: d.data().name || d.id }));
          setTemplates(tpl);
          setSelectedTemplateId(restoredTemplateRef.current || tpl[0]?.id || '');
          restoredTemplateRef.current = null;
        } catch (e) { console.warn('[PipelineTest] load templates gagal:', e); }
      })();
    } else {
      setTemplates([]);
      setSelectedTemplateId('');
    }
  }, [selectedModuleId]);

  // Load library items (system + my products) bila picker dibuka
  useEffect(() => {
    if (!pickerOpen || sysItems.length || ownItems.length) return;
    setLibLoading(true);
    (async () => {
      const email = auth.currentUser?.email;
      try {
        const sysSnap = await getDocs(query(
          collection(db, 'library', LIB_FOLDER, 'items'),
          where('group', '==', 'system'),
          orderBy('number', 'asc')
        ));
        setSysItems(sysSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error('[PipelineTest] load system gagal:', e);
        setError('Gagal load System library: ' + e.message);
      }
      if (email) {
        try {
          const ownSnap = await getDocs(query(
            collection(db, 'library', LIB_FOLDER, 'items'),
            where('group', '==', 'own'),
            where('addedBy', '==', email),
            orderBy('number', 'asc')
          ));
          setOwnItems(ownSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) {
          console.warn('[PipelineTest] load my products gagal:', e);
        }
      }
      setLibLoading(false);
    })();
  }, [pickerOpen]);

  const filteredItems = useMemo(() => {
    const base = libTab === 'own' ? ownItems : sysItems;
    const q = search.trim().toLowerCase();
    let result = q ? base.filter(it => (it.name || '').toLowerCase().includes(q)) : [...base];
    // Sort — logik SAMA macam LibraryView (page Products)
    if (libSort === 'name') {
      result = [...result].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else if (libSort === 'date') {
      result = [...result].sort((a, b) => {
        const da = a.updatedAt?.toDate?.() || a.addedAt?.toDate?.() || new Date(0);
        const db2 = b.updatedAt?.toDate?.() || b.addedAt?.toDate?.() || new Date(0);
        return db2 - da;
      });
    } else {
      result = [...result].sort((a, b) => (a.number || 0) - (b.number || 0));
    }
    return result;
  }, [sysItems, ownItems, libTab, search, libSort]);

  const pickProduct = async (it) => {
    const base = {
      _id: it.id,
      name: it.name,
      link: it.link || '',
      price: it.price || '',
      originalPrice: it.originalPrice || '',
      discount: it.discount || '',
      imageBase64: it.imageBase64 || null,
      imageUrl: it.imageUrl || null,
      gender: null,
      _number: it.number,
      intelligenceData: null,
      scrapedData: null,
      hasData: false,
    };
    setPickerOpen(false);
    setResult(null);
    setError(null);
    setProduct(base);
    // Ambil payload (intelligenceJson + scrapedData) untuk Test B — kalau item ada data.
    if (it.hasIntelligence || it.hasScrapedData || it.intelligenceJson) {
      try {
        let intel = it.intelligenceJson || null;
        let scraped = null;
        if (!intel || it.hasScrapedData) {
          const payload = await loadLibraryPayload(LIB_FOLDER, it.id);
          intel = intel || payload?.intelligenceJson || null;
          scraped = payload?.scrapedData || null;
        }
        setProduct(p => (p && p._id === it.id) ? {
          ...p,
          intelligenceData: intel,
          scrapedData: scraped,
          hasData: !!(intel?.imageAnalysis || intel?.webResearch || scraped),
        } : p);
      } catch (e) {
        console.warn('[PipelineTest] payload fetch gagal:', e);
      }
    }
  };

  const runTest = async () => {
    if (!product || !selectedModuleId) return;
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const call = httpsCallable(functions, 'pipelineTestGenerate', { timeout: 300000 });
      const { intelligenceData, scrapedData, hasData, _id, _number, ...cleanProduct } = product;
      const tpl = selectedModuleId === 'goyang' ? { templateId: selectedTemplateId || null } : {};
      const base = { product: cleanProduct, moduleId: selectedModuleId, avatar: null, subModuleId: null, ...tpl };
      let payload;
      if (topTab === 'prompt') {
        payload = { ...base, mode: 'prompt', testInstruction: newPrompt, model: promptModel, reasoning: promptReasoning, verbosity: promptVerbosity, maxTokens: Number(promptMaxTokens) || 8000, vision: promptVision === true, ayatWajib: promptAyatWajib !== false, intelligenceData: intelligenceData || null, scrapedData: scrapedData || null };
      } else if (mode === 'stage3') {
        payload = { ...base, mode: 'stage3', intelligenceData: intelligenceData || null, scrapedData: scrapedData || null };
      } else {
        payload = { ...base, mode: 'full' };
      }
      const res = await call(payload);
      const data = res.data;
      setResult(data);
      const jimatCost = data.optimized?.totalCostUSD ?? data.variants?.[0]?.totalCostUSD;
      setRuns(prev => [{
        id: Date.now(),
        mode: topTab === 'prompt' ? 'prompt' : mode,
        model: topTab === 'prompt' ? promptModel : null,
        reasoning: topTab === 'prompt' ? promptReasoning : null,
        vision: topTab === 'prompt' ? !!data?.meta?.vision : false,
        ayatWajib: topTab === 'prompt' ? data?.meta?.ayatWajib !== false : true,
        newPrompt: topTab === 'prompt' ? newPrompt : null,
        promptSemasa: topTab === 'prompt' ? currentMasterPrompt : null, // untuk Kesimpulan AI bila run dibuka semula
        module: modules.find(m => m.id === selectedModuleId)?.name || selectedModuleId,
        produk: product.name,
        sekarang: usd(data.current?.totalCostUSD),
        jimat: usd(jimatCost),
        turun: savingsPct(data.current?.totalCostUSD, jimatCost),
        result: data,
      }, ...prev].slice(0, 15)); // simpan 15 run terkini
    } catch (e) {
      console.error('[PipelineTest] run gagal:', e);
      setError(e.message || String(e));
    } finally {
      setRunning(false);
    }
  };

  // Vision kolum kanan: gambar mesti sah (semakan kembar `bersihGambar` di server) — kalau tidak, Run dikunci dengan sebab.
  const visionHalangan = topTab === 'prompt' && promptVision && product
    ? (selectedModuleId === 'flownity' ? 'Vision belum disokong untuk FLOWNITY (Gemini).' : (gambarProduk(product).ralat || null))
    : null;

  const curTotal = result?.current?.totalCostUSD || 0;
  const optTotal = result?.optimized?.totalCostUSD || 0;
  const curTok = result?.current?.writerOutputTokens || 0;
  const optTok = result?.optimized?.writerOutputTokens || 0;
  const reasoningApplies = result?.meta?.reasoningApplies !== false;
  const isPromptMode = result?.meta?.mode === 'prompt';
  const namaModuleRun = modules.find(m => m.id === result?.meta?.moduleId)?.name || result?.meta?.moduleId || '';

  // ── Kesimpulan AI: run yang sedang dipaparkan + prompt yang DIPAKAI oleh run itu ──
  const runSemasa = result ? runs.find(r => r.result === result) : null;
  const dataProdukRun = (product && result?.meta?.productName && product.name === result.meta.productName) ? teksDataProduk(product) : '';
  // Simpan kesimpulan pada run ASALNYA (ikut identiti objek) — ia sudah dibayar, jadi
  // mesti kekal walaupun admin sudah membuka run lain sementara menunggu.
  const simpanKesimpulan = (untukResult, data) => {
    const baharu = { ...untukResult, kesimpulanAi: data };
    setRuns(prev => prev.map(r => (r.result === untukResult ? { ...r, result: baharu } : r)));
    setResult(cur => (cur === untukResult ? baharu : cur));
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">

        {/* Header */}
        <div className="flex items-center gap-2.5 mb-1">
          <h1 className="text-[22px] font-bold text-slate-800 m-0">Makmal ujian jimat OpenAI</h1>
          <span className="inline-flex items-center gap-1 text-[12px] bg-indigo-50 text-indigo-600 px-2.5 py-1 rounded-lg font-bold">
            <span className="material-symbols-outlined text-[14px]">lock</span>admin sahaja
          </span>
        </div>
        <p className="text-[14px] text-slate-500 leading-relaxed m-0 mb-5">
          Test tanpa sentuh workspace user. Tak potong kuota, tak simpan produk, tak kacau data kos.
        </p>

        {/* Tab atas */}
        <div className="flex gap-1 border-b border-slate-200 mb-5">
          {[['pipeline', 'Ujian Pipeline'], ['prompt', 'Ujian Master Prompt'], ['archv2', 'Ujian Prompt Architecture V2']].map(([id, label]) => (
            <button
              key={id}
              onClick={() => { setTopTab(id); setResult(null); setError(null); if (id === 'archv2') setV2Dibuka(true); }}
              className={`px-4 py-2 text-[14px] font-semibold border-b-2 -mb-px ${topTab === id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            >{label}</button>
          ))}
        </div>

        {/* Jenis ujian (Ujian Pipeline sahaja) */}
        {topTab === 'pipeline' && (
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <button
            onClick={() => { setMode('full'); setResult(null); }}
            className={`flex-1 text-left rounded-xl border p-3.5 transition ${mode === 'full' ? 'border-indigo-500 border-2 bg-indigo-50/40' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
          >
            <p className={`text-[13.5px] font-bold m-0 ${mode === 'full' ? 'text-indigo-700' : 'text-slate-700'}`}>A · Pipeline penuh</p>
            <p className="text-[12px] text-slate-500 m-0 mt-0.5">Stage 1+2+3 · uji Fix 1 (reasoning) · research kekal gpt-4.1-mini</p>
          </button>
          <button
            onClick={() => { setMode('stage3'); setResult(null); }}
            className={`flex-1 text-left rounded-xl border p-3.5 transition ${mode === 'stage3' ? 'border-indigo-500 border-2 bg-indigo-50/40' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
          >
            <p className={`text-[13.5px] font-bold m-0 ${mode === 'stage3' ? 'text-indigo-700' : 'text-slate-700'}`}>B · Skip Stage 1+2</p>
            <p className="text-[12px] text-slate-500 m-0 mt-0.5">Sekarang = pipeline penuh · Jimat = guna scrape + reasoning low</p>
          </button>
        </div>
        )}

        {/* Tab ketiga — seni bina prompt modular (fail sendiri). Guna picker produk + senarai model makmal ini. */}
        {v2Dibuka && (
          <div className={topTab === 'archv2' ? '' : 'hidden'}>
            <ArchV2Makmal
              produkPustaka={product}
              onPilihProduk={() => setPickerOpen(true)}
              labModels={LAB_MODELS}
              normalizeReasoningFor={normalizeReasoningFor}
            />
          </div>
        )}

        {/* Dua tab asal (Ujian Pipeline · Ujian Master Prompt) — disorok bila tab V2 dibuka */}
        {topTab !== 'archv2' && (<>
        {/* Kawalan */}
        <div className="rounded-xl bg-white border border-slate-200 p-5 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
            <div>
              <label className="block text-[13px] text-slate-500 mb-1.5">Module</label>
              <select
                value={selectedModuleId}
                onChange={(e) => { setSelectedModuleId(e.target.value); setResult(null); }}
                className="w-full h-9 px-3 text-[14px] border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                {modules.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[13px] text-slate-500 mb-1.5">Produk ujian</label>
              <button onClick={() => setPickerOpen(true)} className="w-full h-9 flex items-center justify-center gap-2 text-[13px] font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">
                <span className="material-symbols-outlined text-[16px]">folder_open</span>{product ? 'Tukar produk' : 'Pilih dari library'}
              </button>
            </div>
            <button
              onClick={runTest}
              disabled={!product || running || ((mode === 'stage3' || topTab === 'prompt') && !product.hasData) || (topTab === 'prompt' && !newPrompt.trim()) || !!visionHalangan}
              title={visionHalangan || undefined}
              className="h-9 flex items-center gap-2 px-5 text-[13px] font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-[16px]">{running ? 'hourglass_empty' : 'play_arrow'}</span>{running ? 'Menjalankan…' : 'Run ujian'}
            </button>
          </div>

          {/* Template GOYANG (fragment hook/bg) */}
          {selectedModuleId === 'goyang' && templates.length > 0 && (
            <div className="mt-3">
              <label className="block text-[13px] text-slate-500 mb-1.5">Template GOYANG (fragment hook/bg)</label>
              <select value={selectedTemplateId} onChange={(e) => { setSelectedTemplateId(e.target.value); setResult(null); }}
                className="w-full h-9 px-3 text-[14px] border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500">
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}

          {/* Kawalan khas Ujian Master Prompt */}
          {topTab === 'prompt' && (
            <>
              <p className="text-[12px] text-slate-500 mt-3 mb-1.5">Setting model untuk kolum KANAN — kolum kiri sentiasa ikut setting production sebenar:</p>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div>
                  <label className="block text-[12px] text-slate-400 mb-1">Model</label>
                  <select value={promptModel}
                    onChange={(e) => {
                      const m = e.target.value;
                      setPromptModel(m);
                      // Tukar model = nama tahap reasoning berubah ('minimal' ↔ 'none').
                      setPromptReasoning(prev => normalizeReasoningFor(m, prev));
                      setResult(null);
                    }}
                    className="w-full h-9 px-3 text-[14px] border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500">
                    {LAB_MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[12px] text-slate-400 mb-1">Reasoning effort</label>
                  <select value={promptReasoning} onChange={(e) => { setPromptReasoning(e.target.value); setResult(null); }}
                    className="w-full h-9 px-3 text-[14px] border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500">
                    {reasoningLevelsFor(promptModel).map(lv => <option key={lv} value={lv}>{lv}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[12px] text-slate-400 mb-1">Verbosity</label>
                  <select value={promptVerbosity} onChange={(e) => { setPromptVerbosity(e.target.value); setResult(null); }}
                    className="w-full h-9 px-3 text-[14px] border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500">
                    <option value="low">low</option>
                    <option value="medium">medium</option>
                    <option value="high">high</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[12px] text-slate-400 mb-1">Max output tokens</label>
                  <input type="number" min="256" max="8000" step="50" value={promptMaxTokens}
                    onChange={(e) => { setPromptMaxTokens(e.target.value); setResult(null); }}
                    className="w-full h-9 px-3 text-[14px] border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                </div>
              </div>
              {/* Vision — kolum KANAN sahaja (kiri kekal production: teks analisis gambar lama) */}
              <div className={`mt-3 rounded-lg border px-3 py-2.5 ${promptVision ? 'border-indigo-200 bg-indigo-50/40' : 'border-slate-200'}`}>
                <div className="flex items-start gap-3">
                  <button
                    role="switch" aria-checked={promptVision} onClick={() => { setPromptVision(v => !v); setResult(null); }}
                    className={`mt-0.5 w-9 h-5 rounded-full relative flex-shrink-0 transition-colors ${promptVision ? 'bg-indigo-600' : 'bg-slate-300'}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${promptVision ? 'left-[18px]' : 'left-0.5'}`} />
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-slate-800 m-0">Vision — send product photo to the AI (right column)</p>
                    <p className="text-[11.5px] text-slate-500 leading-snug m-0 mt-0.5">
                      Kolum kanan tengok gambar sebenar + data produk dalam SATU call. Kalau data bercanggah dengan gambar (cth bilangan bekas), AI ikut gambar.
                      Kolum kiri kekal production (tanpa gambar). Kos gambar masuk dalam "Kos / call" kolum kanan (± 700–1,000 token).
                    </p>
                    {promptVision && product && (visionHalangan
                      ? <p className="text-[12px] text-amber-700 m-0 mt-1.5">{visionHalangan}</p>
                      : <p className="text-[12px] text-emerald-700 m-0 mt-1.5">✓ Gambar produk akan dihantar bersama prompt kolum kanan.</p>)}
                  </div>
                </div>
              </div>
              {/* Ayat wajib module — kolum KANAN sahaja (kiri kekal production: sentiasa ON) */}
              <div className={`mt-2 rounded-lg border px-3 py-2.5 ${promptAyatWajib ? 'border-slate-200' : 'border-amber-200 bg-amber-50/40'}`}>
                <div className="flex items-start gap-3">
                  <button
                    role="switch" aria-checked={promptAyatWajib} onClick={() => { setPromptAyatWajib(v => !v); setResult(null); }}
                    className={`mt-0.5 w-9 h-5 rounded-full relative flex-shrink-0 transition-colors ${promptAyatWajib ? 'bg-indigo-600' : 'bg-slate-300'}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${promptAyatWajib ? 'left-[18px]' : 'left-0.5'}`} />
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-slate-800 m-0">Module mandatory append (right column)</p>
                    <p className="text-[11.5px] text-slate-500 leading-snug m-0 mt-0.5">
                      {promptAyatWajib
                        ? 'ON — ayat wajib module (gambar + video) ditambah di hujung prompt kolum kanan, sama macam production.'
                        : 'OFF — kolum kanan TANPA ayat wajib module: hanya hasil master prompt baru. Kolum kiri kekal production (ada ayat wajib).'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[13px] text-slate-500">Master prompt baru (edit → uji) — draf auto-save</label>
                  <button onClick={() => { setNewPrompt(currentMasterPrompt); delete draftsRef.current[selectedModuleId]; }} className="text-[12px] text-indigo-600 hover:underline">Reset ke prompt semasa</button>
                </div>
                <textarea value={newPrompt} onChange={(e) => { setNewPrompt(e.target.value); draftsRef.current[selectedModuleId] = e.target.value; }} rows={10}
                  placeholder="Master prompt baru untuk module ni…"
                  className="w-full px-3 py-2 text-[13px] font-mono border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-y" />
                <p className="text-[11px] text-slate-400 m-0 mt-1">Prefilled prompt semasa. <span className="font-semibold">Kiri = production sebenar</span> (prompt semasa + model &amp; setting production) · <span className="font-semibold">Kanan = ujian</span> (prompt baru + model &amp; setting pilihan atas). Dua-dua guna data produk yang SAMA, jadi beza datang dari prompt + model sahaja. Nak uji MODEL sahaja: biarkan prompt ni tak diubah, tukar dropdown Model.</p>
              </div>
            </>
          )}

          {(mode === 'stage3' || topTab === 'prompt') && product && !product.hasData && (
            <p className="text-[12px] text-amber-600 mt-3 m-0">Produk ni tiada data intelligence/scrape. Ujian ni perlu produk berlabel <span className="font-semibold">READY</span>.</p>
          )}

          {product && (
            <div className="flex items-center gap-3 mt-4 pt-4 border-t border-slate-100">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 overflow-hidden">
                {product.imageUrl
                  ? <img src={product.imageUrl} alt="" className="w-full h-full object-cover" />
                  : <span className="material-symbols-outlined text-[20px]">image</span>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-semibold text-slate-800 m-0 truncate">{product.name}</p>
                <p className="text-[12px] text-slate-400 m-0">dari library{product.price ? ` · ${product.price}` : ''}</p>
              </div>
              {product.hasData
                ? <span className="text-[11px] bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded font-semibold whitespace-nowrap">ada data</span>
                : <span className="text-[11px] bg-slate-100 text-slate-400 px-2 py-0.5 rounded whitespace-nowrap">tiada data</span>}
            </div>
          )}

          {product && (product.intelligenceData?.webResearch || product.intelligenceData?.imageAnalysis || product.scrapedData) && (
            <details className="mt-3 pt-3 border-t border-slate-100">
              <summary className="text-[12px] font-semibold text-indigo-600 cursor-pointer">Lihat data yang disuap ke AI (untuk sahkan claim spt "tahan UV setahun")</summary>
              <div className="mt-2 space-y-3">
                {product.intelligenceData?.webResearch && (
                  <div>
                    <p className="text-[11px] text-slate-400 m-0 mb-1">Web research / scrape ({product.intelligenceData.webResearch.length} aksara)</p>
                    <pre className="text-[11.5px] text-slate-600 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap break-words m-0 max-h-72 overflow-y-auto">{product.intelligenceData.webResearch}</pre>
                  </div>
                )}
                {product.intelligenceData?.imageAnalysis && (
                  <div>
                    <p className="text-[11px] text-slate-400 m-0 mb-1">Vision analysis ({product.intelligenceData.imageAnalysis.length} aksara)</p>
                    <pre className="text-[11.5px] text-slate-600 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap break-words m-0 max-h-56 overflow-y-auto">{product.intelligenceData.imageAnalysis}</pre>
                  </div>
                )}
                {product.scrapedData && (
                  <div>
                    <p className="text-[11px] text-slate-400 m-0 mb-1">Raw scrape data</p>
                    <pre className="text-[11.5px] text-slate-600 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap break-words m-0 max-h-72 overflow-y-auto">{JSON.stringify(product.scrapedData, null, 2)}</pre>
                  </div>
                )}
              </div>
            </details>
          )}
        </div>

        {running && (
          <div className="rounded-xl bg-white border border-slate-200 p-8 text-center mb-5">
            <span className="material-symbols-outlined text-[32px] text-indigo-500 animate-spin">progressive_activity</span>
            <p className="text-[14px] text-slate-500 mt-2 m-0">{topTab === 'prompt' ? 'Menjalankan dua kolum serentak (production + ujian)…' : mode === 'stage3' ? 'Menjalankan baseline penuh + 3 variant reasoning…' : 'Menjalankan pipeline dua kali (sekarang + jimat)…'} boleh ambil 30 saat – 2 minit.</p>
          </div>
        )}

        {error && (
          <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 mb-5 text-[13px] text-rose-700">
            <span className="font-bold">Ralat:</span> {error}
          </div>
        )}

        {result && !running && (
          <>
            {result.variants ? (
              /* ── TEST B: baseline pipeline penuh + 3 variant reasoning ── */
              <>
                <div className="mb-3">
                  <ResultColumn title="Setting sekarang (pipeline penuh)" icon="settings" col={result.current} accent={false} />
                </div>
                <p className="text-[13px] font-semibold text-slate-700 m-0 mb-2">Setting jimat — 3 tahap reasoning (guna data scrape)</p>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                  {result.variants.map((v) => (
                    <ResultColumn key={v.level} title={`Reasoning: ${v.level}`} icon="bolt" col={v} accent />
                  ))}
                </div>
                <div className="mt-3 rounded-xl bg-white border border-slate-200 p-4 overflow-x-auto">
                  <p className="text-[13px] font-semibold text-slate-700 m-0 mb-2">Ringkasan — jimat vs pipeline penuh sekarang</p>
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr className="text-left text-slate-400 border-b border-slate-100">
                        <th className="py-2 pr-4 font-semibold">Reasoning</th>
                        <th className="py-2 pr-4 font-semibold">Kos / call</th>
                        <th className="py-2 pr-4 font-semibold">{DAILY_CALLS.toLocaleString()} call/hari</th>
                        <th className="py-2 pr-4 font-semibold">Token penulis</th>
                        <th className="py-2 font-semibold">Jimat vs sekarang</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-slate-50 text-slate-600">
                        <td className="py-2 pr-4 font-semibold text-slate-700">sekarang (penuh)</td>
                        <td className="py-2 pr-4">{usd(result.current?.totalCostUSD)}</td>
                        <td className="py-2 pr-4 whitespace-nowrap">{usdDaily(result.current?.totalCostUSD)}/hari · {rmMonthly(result.current?.totalCostUSD)}/bln</td>
                        <td className="py-2 pr-4">{Number(result.current?.writerOutputTokens || 0).toLocaleString()}</td>
                        <td className="py-2 text-slate-400">—</td>
                      </tr>
                      {result.variants.map((v) => (
                        <tr key={v.level} className="border-b border-slate-50 text-slate-600">
                          <td className="py-2 pr-4 font-semibold text-slate-700">{v.level}</td>
                          <td className="py-2 pr-4 text-emerald-600 font-semibold">{usd(v.totalCostUSD)}</td>
                          <td className="py-2 pr-4 text-emerald-600 font-semibold whitespace-nowrap">{usdDaily(v.totalCostUSD)}/hari · {rmMonthly(v.totalCostUSD)}/bln</td>
                          <td className="py-2 pr-4">{Number(v.writerOutputTokens || 0).toLocaleString()}</td>
                          <td className="py-2 text-emerald-600 font-semibold">−{savingsPct(result.current?.totalCostUSD, v.totalCostUSD)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              /* ── TEST A: current vs optimized ── */
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <ResultColumn title={isPromptMode ? 'Master prompt sekarang' : 'Setting sekarang'} icon="settings" col={result.current} accent={false}
                    binaTeksSalinan={isPromptMode ? () => binaTeksSalinan({ result, sisi: 'kiri', namaModule: namaModuleRun, usdKeMyr: USD_TO_MYR }) : null} />
                  <ResultColumn
                    title={isPromptMode ? 'Master prompt baru' : 'Setting jimat'}
                    icon="bolt"
                    col={result.optimized}
                    accent={true}
                    binaTeksSalinan={isPromptMode ? () => binaTeksSalinan({ result, sisi: 'kanan', namaModule: namaModuleRun, usdKeMyr: USD_TO_MYR }) : null}
                    note={!isPromptMode && !reasoningApplies ? 'FLOWNITY guna Gemini — Fix 1 (reasoning) tak apply. Beza datang dari research sahaja.' : null}
                  />
                </div>
                {isPromptMode ? (
                  <div className="flex items-center gap-3 mt-3 rounded-xl bg-slate-100 px-5 py-3.5">
                    <span className="material-symbols-outlined text-[22px] text-slate-500">compare_arrows</span>
                    <div>
                      <p className="text-[13px] text-slate-600 m-0">Banding output dua kolum — nilai prompt baru lebih bagus ke tak. Kos: lama <b>{usd(curTotal)}</b> vs baru <b>{usd(optTotal)}</b> · token: <b>{Number(curTok).toLocaleString()}</b> vs <b>{Number(optTok).toLocaleString()}</b>.</p>
                      {result?.meta?.dataSama && (
                        <p className="text-[11.5px] text-slate-400 m-0 mt-0.5">Kolum kiri (production): model <b>{result?.meta?.baselineModel || '—'}</b> · reasoning <b>{result?.meta?.baselineReasoning || '—'}</b> · verbosity <b>{result?.meta?.baselineVerbosity || '—'}</b> · max <b>{result?.meta?.baselineMaxTokens}</b> tokens · data sama dengan kolum kanan</p>
                      )}
                      <p className="text-[11.5px] text-slate-400 m-0 mt-0.5">Kolum kanan: model <b>{result?.meta?.optimizedModel || '—'}</b> · reasoning <b>{result?.meta?.reasoning}</b> · verbosity <b>{result?.meta?.verbosity || '—'}</b> · max <b>{result?.meta?.maxTokens}</b> tokens{result?.meta?.vision ? <> · <b className="text-indigo-600">vision ON</b> (gambar dihantar)</> : ''}{result?.meta?.ayatWajib === false ? <> · <b className="text-amber-600">ayat wajib OFF</b></> : ''}{Number(optTok) >= (result?.meta?.maxTokens || 0) ? ' ⚠ output cecah had (mungkin terpotong)' : ''}</p>
                      {result?.meta?.optimizedModel && result?.meta?.optimizedModel !== result?.meta?.baselineModel && !result?.meta?.geminiUsed && (
                        <p className="text-[11.5px] text-indigo-500 m-0 mt-0.5">Dua kolum guna model BERBEZA — beza kos &amp; ayat datang dari model, bukan prompt sahaja.</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-4 mt-3 rounded-xl bg-emerald-50 border border-emerald-100 px-5 py-3.5">
                    <span className="material-symbols-outlined text-[24px] text-emerald-600">trending_down</span>
                    <div className="flex gap-8 flex-wrap">
                      <div><p className="text-[12px] text-emerald-600 m-0">Jimat kos</p><p className="text-[18px] font-bold text-emerald-700 m-0">−{savingsPct(curTotal, optTotal)}%</p></div>
                      <div><p className="text-[12px] text-emerald-600 m-0">Token turun</p><p className="text-[18px] font-bold text-emerald-700 m-0">−{savingsPct(curTok, optTok)}%</p></div>
                      <div><p className="text-[12px] text-slate-500 m-0">Kualiti output</p><p className="text-[14px] text-slate-700 m-0">baca dua kolum, nilai sendiri ↑</p></div>
                    </div>
                  </div>
                )}
                {isPromptMode && (
                  <KesimpulanAiMakmal
                    key={runSemasa?.id || 'semasa'}
                    result={result}
                    promptSemasa={runSemasa ? (runSemasa.promptSemasa || '') : currentMasterPrompt}
                    promptBaru={runSemasa ? (runSemasa.newPrompt || '') : newPrompt}
                    dataProduk={dataProdukRun}
                    namaModule={runSemasa?.module || ''}
                    onSiap={simpanKesimpulan}
                  />
                )}
              </>
            )}

            {/* Semak ayat wajib append */}
            {result.appends && (result.appends.image || result.appends.video) && (
              <details className="mt-3 rounded-xl bg-white border border-slate-200 p-4">
                <summary className="text-[13px] font-semibold text-slate-700 cursor-pointer">Ayat wajib append (dah masuk dalam prompt di atas) — klik untuk semak</summary>
                <div className="mt-3 space-y-3">
                  <div>
                    <p className="text-[12px] text-slate-400 m-0 mb-1">Image append {result.appends.image ? `(${result.appends.image.length} aksara)` : ''}</p>
                    <pre className="text-[12px] text-slate-600 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap break-words m-0">{result.appends.image || '(tiada)'}</pre>
                  </div>
                  <div>
                    <p className="text-[12px] text-slate-400 m-0 mb-1">Video append {result.appends.video ? `(${result.appends.video.length} aksara)` : ''}</p>
                    <pre className="text-[12px] text-slate-600 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap break-words m-0">{result.appends.video || '(tiada)'}</pre>
                  </div>
                </div>
              </details>
            )}
          </>
        )}

        {/* Sejarah run — disimpan localStorage, klik untuk buka semula */}
        {runs.length > 0 && (
          <div className="rounded-xl bg-white border border-slate-200 p-5 mt-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[14px] font-semibold text-slate-800 m-0">Sejarah run (auto-save — klik baris untuk buka semula)</p>
              <button onClick={() => setRuns([])} className="text-[12px] text-rose-500 hover:underline">Padam sejarah</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-slate-400 border-b border-slate-100">
                    <th className="py-2 pr-4 font-semibold">Masa</th>
                    <th className="py-2 pr-4 font-semibold">Jenis</th>
                    <th className="py-2 pr-4 font-semibold">Module</th>
                    <th className="py-2 pr-4 font-semibold">Produk</th>
                    <th className="py-2 pr-4 font-semibold">Kos</th>
                    <th className="py-2 font-semibold"></th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr
                      key={r.id}
                      onClick={() => {
                        setResult(r.result || null);
                        setError(null);
                        if (r.mode === 'prompt') {
                          setTopTab('prompt');
                          const rm = r.model || 'gpt-5-mini';
                          setPromptModel(rm);
                          if (r.reasoning) setPromptReasoning(normalizeReasoningFor(rm, r.reasoning));
                          setPromptVision(r.vision === true);
                          setPromptAyatWajib(r.ayatWajib !== false);
                          if (r.newPrompt) { setNewPrompt(r.newPrompt); draftsRef.current[selectedModuleId] = r.newPrompt; }
                        } else {
                          setTopTab('pipeline');
                          if (r.mode) setMode(r.mode);
                        }
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="border-b border-slate-50 text-slate-600 cursor-pointer hover:bg-indigo-50/40"
                    >
                      <td className="py-2.5 pr-4 text-slate-400 whitespace-nowrap">{new Date(r.id).toLocaleString('ms-MY', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                      <td className="py-2.5 pr-4 whitespace-nowrap">{r.mode === 'prompt' ? `prompt · ${r.model || 'gpt-5-mini'} (${r.reasoning})${r.vision ? ' · vision' : ''}${r.ayatWajib === false ? ' · tanpa ayat wajib' : ''}` : r.mode}</td>
                      <td className="py-2.5 pr-4 font-semibold text-slate-700 whitespace-nowrap">{r.module}</td>
                      <td className="py-2.5 pr-4"><span className="block truncate max-w-[220px]">{r.produk}</span></td>
                      <td className="py-2.5 pr-4 whitespace-nowrap">{r.sekarang} → <span className="text-emerald-600 font-semibold">{r.jimat}</span></td>
                      <td className="py-2.5 text-indigo-500 font-semibold whitespace-nowrap">buka →</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </>)}

      </div>

      {/* Picker modal */}
      {pickerOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setPickerOpen(false)}>
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <p className="text-[15px] font-semibold text-slate-800 m-0">Pilih produk dari library</p>
              <button onClick={() => setPickerOpen(false)} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
            </div>
            <div className="flex gap-1 px-5 pt-3">
              <button
                onClick={() => setLibTab('system')}
                className={`px-3 py-1.5 text-[13px] font-semibold rounded-lg ${libTab === 'system' ? 'bg-indigo-50 text-indigo-600' : 'text-slate-500 hover:bg-slate-50'}`}
              >System ({sysItems.length})</button>
              <button
                onClick={() => setLibTab('own')}
                className={`px-3 py-1.5 text-[13px] font-semibold rounded-lg ${libTab === 'own' ? 'bg-indigo-50 text-indigo-600' : 'text-slate-500 hover:bg-slate-50'}`}
              >My Products ({ownItems.length})</button>
            </div>
            <div className="px-5 py-3 border-b border-slate-100 flex gap-2">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cari nama produk…"
                className="flex-1 h-9 px-3 text-[14px] border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
              <select
                value={libSort}
                onChange={(e) => setLibSort(e.target.value)}
                className="h-9 px-2 text-[13px] border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value="number">Sort: Number</option>
                <option value="name">Sort: Name</option>
                <option value="date">Sort: Terbaru</option>
              </select>
            </div>
            <div className="flex-1 overflow-y-auto px-3 py-2">
              {libLoading && <p className="text-center text-slate-400 text-[13px] py-8">Memuatkan library…</p>}
              {!libLoading && filteredItems.length === 0 && <p className="text-center text-slate-400 text-[13px] py-8">Tiada produk dijumpai.</p>}
              {filteredItems.map((it) => (
                <button key={it.id} onClick={() => pickProduct(it)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-left">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 overflow-hidden flex-shrink-0">
                    {it.imageUrl ? <img src={it.imageUrl} alt="" className="w-full h-full object-cover" /> : <span className="material-symbols-outlined text-[18px]">image</span>}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[13.5px] font-semibold text-slate-700 m-0 truncate">{it.number ? `#${it.number} · ` : ''}{it.name}</p>
                    <p className="text-[12px] text-slate-400 m-0 truncate">{it.price || 'tiada harga'}{it.link ? ' · ada link' : ''}</p>
                  </div>
                  <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded font-semibold flex-shrink-0 ${(it.hasIntelligence || it.intelligenceJson) ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>{(it.hasIntelligence || it.intelligenceJson) ? 'READY' : 'PENDING'}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
